import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Package, Plus, Search, Filter, Download, Edit, 
  Trash2, AlertTriangle, Eye, Archive, TrendingUp,
  QrCode, Camera, MapPin, Calendar, Euro, Package2,
  FileText, ArrowLeft, Upload, FileSpreadsheet, RefreshCw, X
} from 'lucide-react';
import axios from 'axios';
import { usePermissions } from '../contexts/PermissionsContext';
import HandoverProtocol from './HandoverProtocol';
import MaterialHandoverProtocol from './MaterialHandoverProtocol';
import MaterialReceiptProtocol from './MaterialReceiptProtocol';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

const Inventory = () => {
  const { hasPermission, isTechniker } = usePermissions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [stats, setStats] = useState(null);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  // Techniker starten direkt im Material-Handover-Modus
  const [view, setView] = useState(isTechniker() ? 'material-handover' : 'overview'); // 'overview', 'items', 'alerts', 'handover', 'material-handover', 'material-receipt', 'change-log', 'add'

  // Categories, Status, and Location options
  const categories = [
    { value: 'werkzeuge', label: 'Werkzeuge' },
    { value: 'maschinen', label: 'Maschinen' },
    { value: 'materialien', label: 'Materialien' },
    { value: 'verbrauchsartikel', label: 'Verbrauchsartikel' },
    { value: 'ersatzteile', label: 'Ersatzteile' },
    { value: 'sonstige', label: 'Sonstige' }
  ];

  const statuses = [
    { value: 'verfügbar', label: 'Verfügbar', color: 'bg-green-100 text-green-800' },
    { value: 'ausgeliehen', label: 'Ausgeliehen', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'wartung', label: 'Wartung', color: 'bg-orange-100 text-orange-800' },
    { value: 'defekt', label: 'Defekt', color: 'bg-red-100 text-red-800' },
    { value: 'ausrangiert', label: 'Ausrangiert', color: 'bg-gray-100 text-gray-800' }
  ];

  const locations = [
    { value: 'hauptlager', label: 'Hauptlager' },
    { value: 'fahrzeug', label: 'Fahrzeug' },
    { value: 'baustelle', label: 'Baustelle' },
    { value: 'werkstatt', label: 'Werkstatt' },
    { value: 'büro', label: 'Büro' }
  ];

  // Fetch data
  const fetchItems = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedLocation) params.append('location', selectedLocation);
      if (showLowStockOnly) params.append('low_stock_only', 'true');

      const response = await axios.get(`${BACKEND_URL}/api/inventory?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setItems(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Inventargegenstände:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/inventory/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    }
  };

  const fetchLowStockAlerts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/inventory/alerts/low-stock`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLowStockAlerts(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Bestandswarnungen:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchItems(), fetchStats(), fetchLowStockAlerts()]);
      setLoading(false);
    };
    loadData();
  }, [searchTerm, selectedCategory, selectedStatus, selectedLocation, showLowStockOnly]);

  // Techniker-Zugriffskontrolle: Nur Material-Übergabeprotokoll erlaubt
  useEffect(() => {
    if (isTechniker() && view !== 'material-handover') {
      setView('material-handover');
    }
  }, [isTechniker, view]);

  const getCategoryLabel = (value) => {
    return categories.find(cat => cat.value === value)?.label || value;
  };

  const getStatusInfo = (value) => {
    return statuses.find(status => status.value === value) || { label: value, color: 'bg-gray-100 text-gray-800' };
  };

  const getLocationLabel = (value) => {
    return locations.find(loc => loc.value === value)?.label || value;
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Artikel löschen möchten?')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${BACKEND_URL}/api/inventory/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchItems();
      await fetchStats();
    } catch (error) {
      alert('Fehler beim Löschen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/inventory/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventar_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert('Excel-Export erfolgreich');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Fehler beim Excel-Export');
    }
  };

  const exportPDF = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/inventory/export/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventar_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert('PDF-Export erfolgreich');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Fehler beim PDF-Export');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Bitte wählen Sie eine Datei aus');
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${BACKEND_URL}/api/inventory/import/excel`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert(`Import erfolgreich: ${response.data.imported} erstellt, ${response.data.updated} aktualisiert`);
      if (response.data.errors && response.data.errors.length > 0) {
        console.log('Import-Fehler:', response.data.errors);
        alert(`${response.data.errors.length} Fehler beim Import`);
      }
      
      setShowImportDialog(false);
      setImportFile(null);
      fetchItems();
      fetchStats();
    } catch (error) {
      console.error('Error importing:', error);
      alert('Fehler beim Import: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchItems(), fetchStats(), fetchLowStockAlerts()]);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-full bg-slate-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-12 h-12 text-emerald-600 mx-auto animate-spin" />
          <p className="text-slate-600 mt-2">Lädt Lagerverwaltung...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-6" data-testid="inventory-page">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Package className="w-8 h-8 text-emerald-600" />
            Lagerverwaltung 📦
          </h1>
          <p className="text-slate-600 mt-1">Materialien und Werkzeuge mit QR-Scanner verwalten</p>
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          className="gap-2"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Navigation Tabs - Techniker sehen nur Material-Übergabeprotokoll */}
      <div className="mb-6 flex flex-wrap gap-2">
        {isTechniker() ? (
          // Techniker-Ansicht: Nur Material-Übergabeprotokoll
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">Techniker-Bereich</span>
            </div>
            <p className="text-blue-700 text-sm mb-3">
              Als Techniker haben Sie Zugriff auf Material-Übergabeprotokolle.
            </p>
            <Button 
              variant={view === 'material-handover' ? 'default' : 'outline'} 
              onClick={() => setView('material-handover')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Package className="w-4 h-4 mr-2" />
              Material-Übergabeprotokoll
            </Button>
          </div>
        ) : (
          // Admin/Manager-Ansicht: Alle Tabs
          <>
            <Button 
              variant={view === 'overview' ? 'default' : 'outline'} 
              onClick={() => setView('overview')}
              className="flex items-center gap-2"
            >
              <TrendingUp className="w-4 h-4" />
              Übersicht
            </Button>
            <Button 
              variant={view === 'items' ? 'default' : 'outline'} 
              onClick={() => setView('items')}
              className="flex items-center gap-2"
            >
              <Package2 className="w-4 h-4" />
              Artikel ({items.length})
            </Button>
            <Button 
              variant={view === 'alerts' ? 'default' : 'outline'} 
              onClick={() => setView('alerts')}
              className="flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4" />
              Warnungen ({lowStockAlerts.length})
            </Button>
            <Button 
              variant={view === 'handover' ? 'default' : 'outline'} 
              onClick={() => setView('handover')}
              className="flex items-center gap-2"
            >
              <Package2 className="w-4 h-4" />
              Übergabe Protokoll
            </Button>
            <Button 
              variant={view === 'material-handover' ? 'default' : 'outline'} 
              onClick={() => setView('material-handover')}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Package className="w-4 h-4" />
              Material-Übergabeprotokoll
            </Button>
            <Button 
              variant={view === 'material-receipt' ? 'default' : 'outline'} 
              onClick={() => setView('material-receipt')}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
            >
              <Package2 className="w-4 h-4" />
              Materialeinnahme
            </Button>
            <Button 
              variant={view === 'change-log' ? 'default' : 'outline'} 
              onClick={() => setView('change-log')}
              className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
            >
              <FileText className="w-4 h-4" />
              Änderungslog
            </Button>
            <Button 
              onClick={exportExcel}
              variant="outline"
              className="flex items-center gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Excel Export
            </Button>
            <Button 
              onClick={exportPDF}
              variant="outline"
              className="flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              PDF Export
            </Button>
            <Button 
              onClick={() => setShowImportDialog(true)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Import
            </Button>
            <Button 
              onClick={() => setView('add')}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4" />
              Artikel hinzufügen
            </Button>
          </>
        )}
      </div>

      {/* Import Dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Inventar importieren</h3>
            <p className="text-sm text-slate-600 mb-4">
              Laden Sie eine Excel-Datei (.xlsx) mit Inventardaten hoch. 
              Die Datei sollte folgende Spalten enthalten: Name, Kategorie, Aktueller Bestand, Min. Bestand, Max. Bestand, Standort, Status, Seriennummer, Inventarnummer.
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files[0])}
              className="w-full mb-4 p-2 border rounded"
            />
            {importFile && (
              <p className="text-sm text-emerald-600 mb-4">
                Ausgewählt: {importFile.name}
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportDialog(false);
                  setImportFile(null);
                }}
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleImport}
                disabled={!importFile}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importieren
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Overview */}
      {view === 'overview' && stats && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Gesamt Artikel</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_items}</p>
                  </div>
                  <Package className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Warnungen</p>
                    <p className="text-2xl font-bold text-orange-600">{stats.low_stock_alerts}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Gesamtwert</p>
                    <p className="text-2xl font-bold text-green-600">€{stats.total_inventory_value}</p>
                  </div>
                  <Euro className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Kategorien</p>
                    <p className="text-2xl font-bold text-purple-600">{Object.keys(stats.categories || {}).length}</p>
                  </div>
                  <Archive className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Categories Breakdown */}
          {stats.categories && (
            <Card>
              <CardHeader>
                <CardTitle>Kategorien Übersicht</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(stats.categories).map(([category, count]) => (
                    <div key={category} className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-medium">{getCategoryLabel(category)}</p>
                      <p className="text-2xl font-bold text-emerald-600">{count}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Items View */}
      {view === 'items' && (
        <div className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium mb-2">Suche</label>
                  <Input
                    placeholder="Nach Name, Seriennummer..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Kategorie</label>
                  <select 
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md"
                  >
                    <option value="">Alle Kategorien</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select 
                    value={selectedStatus} 
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md"
                  >
                    <option value="">Alle Status</option>
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Standort</label>
                  <select 
                    value={selectedLocation} 
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md"
                  >
                    <option value="">Alle Standorte</option>
                    {locations.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="lowStock"
                    checked={showLowStockOnly}
                    onChange={(e) => setShowLowStockOnly(e.target.checked)}
                  />
                  <label htmlFor="lowStock" className="text-sm font-medium">Nur niedrige Bestände</label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Inventargegenstände ({items.length})</span>
                <Button onClick={() => setView('add')} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Hinzufügen
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Keine Artikel gefunden</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium">Name</th>
                        <th className="text-left p-3 font-medium">Kategorie</th>
                        <th className="text-left p-3 font-medium">Bestand</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Standort</th>
                        <th className="text-left p-3 font-medium">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const statusInfo = getStatusInfo(item.status);
                        const isLowStock = item.current_stock <= item.min_stock;
                        
                        return (
                          <tr key={item.id} className="border-b hover:bg-slate-50">
                            <td className="p-3">
                              <div>
                                <p className="font-medium">{item.name}</p>
                                {item.serial_number && (
                                  <p className="text-sm text-slate-500">S/N: {item.serial_number}</p>
                                )}
                              </div>
                            </td>
                            <td className="p-3">{getCategoryLabel(item.category)}</td>
                            <td className="p-3">
                              <div className={`${isLowStock ? 'text-red-600 font-bold' : 'text-slate-900'}`}>
                                {item.current_stock} {item.unit}
                                {isLowStock && (
                                  <AlertTriangle className="w-4 h-4 inline ml-1 text-red-500" />
                                )}
                              </div>
                              <p className="text-xs text-slate-500">Min: {item.min_stock}</p>
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${statusInfo.color}`}>
                                {statusInfo.label}
                              </span>
                            </td>
                            <td className="p-3">{getLocationLabel(item.location)}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedItem(item);
                                    setShowEditModal(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteItem(item.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts View */}
      {view === 'alerts' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Bestandswarnungen ({lowStockAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lowStockAlerts.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Keine Bestandswarnungen</p>
                <p className="text-sm text-slate-400 mt-2">Alle Artikel haben ausreichend Bestand</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lowStockAlerts.map((alert) => (
                  <div key={alert.id} className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{alert.name}</p>
                        <p className="text-sm text-slate-600">{getCategoryLabel(alert.category)} • {getLocationLabel(alert.location)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-orange-600">
                          {alert.current_stock} / {alert.min_stock} {alert.unit}
                        </p>
                        <p className="text-sm text-orange-500">{alert.stock_percentage}% vom Mindestbestand</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Item View */}
      {view === 'add' && (
        <AddItemForm 
          onBack={() => setView('items')} 
          onSuccess={() => {
            setView('items');
            fetchItems();
            fetchStats();
          }}
        />
      )}

      {/* Handover Protocol View */}
      {view === 'handover' && (
        <HandoverProtocol 
          onBack={() => setView('overview')}
        />
      )}

      {/* Material Handover Protocol View */}
      {view === 'material-handover' && (
        <MaterialHandoverProtocol 
          onBack={() => setView('overview')}
        />
      )}

      {/* Material Receipt Protocol View */}
      {view === 'material-receipt' && (
        <MaterialReceiptProtocol 
          onBack={() => setView('overview')}
        />
      )}

      {/* Inventory Change Log View */}
      {view === 'change-log' && (
        <InventoryChangeLog onBack={() => setView('overview')} />
      )}

      {/* Edit Item Modal */}
      {showEditModal && selectedItem && (
        <EditItemForm
          item={selectedItem}
          onClose={() => {
            setShowEditModal(false);
            setSelectedItem(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedItem(null);
            fetchItems();
          }}
        />
      )}
    </div>
  );
};

// Inventory Change Log Component
const InventoryChangeLog = ({ onBack }) => {
  const [changeLog, setChangeLog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchChangeLog();
  }, []);

  const fetchChangeLog = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/inventory-change-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChangeLog(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Änderungslogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button onClick={onBack} variant="outline" className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Zurück zur Übersicht
        </Button>
        <h2 className="text-xl font-bold">Lagerbestand Änderungslog</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Automatische Lagerbestandsänderungen ({changeLog.length})
          </CardTitle>
          <p className="text-sm text-gray-600">
            Protokollierung aller Bestandsänderungen durch Material-Einnahmeprotokolle
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Laden...</div>
          ) : changeLog.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Noch keine Lagerbestandsänderungen protokolliert
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">Datum/Zeit</th>
                    <th className="text-left p-3">Protokoll</th>
                    <th className="text-left p-3">Mitarbeiter</th>
                    <th className="text-left p-3">Artikel</th>
                    <th className="text-left p-3">Aktion</th>
                    <th className="text-left p-3">Bestand</th>
                    <th className="text-left p-3">Änderung</th>
                  </tr>
                </thead>
                <tbody>
                  {changeLog.map((entry, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm">{formatDate(entry.timestamp)}</td>
                      <td className="p-3 text-sm font-medium">{entry.protocol_number}</td>
                      <td className="p-3 text-sm">{entry.employee_name}</td>
                      <td className="p-3 text-sm">{entry.item_name}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          entry.action === 'stock_increased' ? 'bg-green-100 text-green-800' :
                          entry.action === 'new_item_created' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {entry.action === 'stock_increased' ? 'Bestand erhöht' :
                           entry.action === 'new_item_created' ? 'Neu erstellt' : entry.action}
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        {entry.previous_stock} → <strong>{entry.new_stock}</strong>
                      </td>
                      <td className="p-3 text-sm text-green-600 font-medium">
                        +{entry.quantity_changed}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Add Item Component
const AddItemForm = ({ onBack, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'werkzeuge',
    subcategory: '',
    serial_number: '',
    inventory_number: '',  // New: Inventarnummer field
    current_stock: 0,
    min_stock: 0,
    max_stock: '',
    unit: 'Stück',
    purchase_price: '',
    current_value: '',
    supplier: '',
    location: 'hauptlager',
    location_details: '',
    status: 'verfügbar',
    manufacturer: '',
    model: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scannerError, setScannerError] = useState(null);

  const categories = [
    { value: 'werkzeuge', label: 'Werkzeuge' },
    { value: 'maschinen', label: 'Maschinen' },
    { value: 'materialien', label: 'Materialien' },
    { value: 'verbrauchsartikel', label: 'Verbrauchsartikel' },
    { value: 'ersatzteile', label: 'Ersatzteile' },
    { value: 'sonstige', label: 'Sonstige' }
  ];

  const locations = [
    { value: 'hauptlager', label: 'Hauptlager' },
    { value: 'fahrzeug', label: 'Fahrzeug' },
    { value: 'baustelle', label: 'Baustelle' },
    { value: 'werkstatt', label: 'Werkstatt' },
    { value: 'büro', label: 'Büro' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      
      // Convert numeric fields
      const submitData = {
        ...formData,
        current_stock: parseInt(formData.current_stock) || 0,
        min_stock: parseInt(formData.min_stock) || 0,
        max_stock: formData.max_stock ? parseInt(formData.max_stock) : null,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        current_value: formData.current_value ? parseFloat(formData.current_value) : null
      };

      await axios.post(`${BACKEND_URL}/api/inventory`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Artikel erfolgreich hinzugefügt!');
      onSuccess();
    } catch (error) {
      alert('Fehler beim Hinzufügen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // QR Code Scanner Functions
  const startQRScanner = async () => {
    try {
      setShowQRScanner(true);
      setScannerError(null);
      
      // Check if browser supports camera access
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setScannerError('Kamera wird von diesem Browser nicht unterstützt');
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera
      });
      
      // Simple implementation - for production, use a library like react-qr-scanner
      // This is a placeholder that demonstrates the concept
      setTimeout(() => {
        // Simulate QR code scan result
        const mockQRResult = `INV-${Date.now()}`;
        handleQRScanResult(mockQRResult);
        
        // Stop camera stream
        stream.getTracks().forEach(track => track.stop());
      }, 3000);
      
    } catch (error) {
      console.error('QR Scanner Error:', error);
      setScannerError('Fehler beim Zugriff auf die Kamera: ' + error.message);
    }
  };

  const handleQRScanResult = (scannedValue) => {
    // Write scanned value to inventory number field
    handleChange('inventory_number', scannedValue);
    setShowQRScanner(false);
    setScannerError(null);
  };

  const stopQRScanner = () => {
    setShowQRScanner(false);
    setScannerError(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Neuen Artikel hinzufügen</span>
          <Button variant="outline" onClick={onBack}>
            Zurück
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Basic Information */}
            <div>
              <label className="block text-sm font-medium mb-2">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Artikel-Name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Kategorie *</label>
              <select 
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full p-2 border border-slate-300 rounded-md"
                required
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Beschreibung</label>
              <Input
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Beschreibung des Artikels"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Seriennummer</label>
              <Input
                value={formData.serial_number}
                onChange={(e) => handleChange('serial_number', e.target.value)}
                placeholder="Seriennummer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Inventarnummer</label>
              <div className="flex gap-2">
                <Input
                  value={formData.inventory_number}
                  onChange={(e) => handleChange('inventory_number', e.target.value)}
                  placeholder="Inventarnummer"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={startQRScanner}
                  disabled={showQRScanner}
                  className="px-3 py-2 flex items-center gap-2"
                  title="QR-Code scannen"
                >
                  <QrCode className="w-4 h-4" />
                  {showQRScanner ? 'Scanne...' : 'QR'}
                </Button>
              </div>
              {scannerError && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {scannerError}
                </div>
              )}
              {showQRScanner && (
                <div className="mt-2 p-4 bg-blue-50 border border-blue-200 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-blue-700 font-medium">QR-Code Scanner aktiv</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={stopQRScanner}
                      className="text-blue-700"
                    >
                      Abbrechen
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <Camera className="w-4 h-4 animate-pulse" />
                    <span>Richten Sie die Kamera auf den QR-Code...</span>
                  </div>
                  <div className="mt-2 text-xs text-blue-500">
                    Hinweis: In der Demo-Version wird ein Mock-Wert generiert. In der Produktion würde hier ein echter QR-Code Scanner verwendet.
                  </div>
                </div>
              )}
            </div>

            {/* Stock Information */}
            <div>
              <label className="block text-sm font-medium mb-2">Aktueller Bestand *</label>
              <Input
                type="number"
                value={formData.current_stock}
                onChange={(e) => handleChange('current_stock', e.target.value)}
                min="0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Mindestbestand *</label>
              <Input
                type="number"
                value={formData.min_stock}
                onChange={(e) => handleChange('min_stock', e.target.value)}
                min="0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Einheit</label>
              <Input
                value={formData.unit}
                onChange={(e) => handleChange('unit', e.target.value)}
                placeholder="Stück, kg, m, l..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Standort</label>
              <select 
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                className="w-full p-2 border border-slate-300 rounded-md"
              >
                {locations.map(loc => (
                  <option key={loc.value} value={loc.value}>{loc.label}</option>
                ))}
              </select>
            </div>

            {/* Additional Information */}
            <div>
              <label className="block text-sm font-medium mb-2">Hersteller</label>
              <Input
                value={formData.manufacturer}
                onChange={(e) => handleChange('manufacturer', e.target.value)}
                placeholder="Hersteller"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Modell</label>
              <Input
                value={formData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                placeholder="Modell"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Einkaufspreis (€)</label>
              <Input
                type="number"
                step="0.01"
                value={formData.purchase_price}
                onChange={(e) => handleChange('purchase_price', e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Lieferant</label>
              <Input
                value={formData.supplier}
                onChange={(e) => handleChange('supplier', e.target.value)}
                placeholder="Lieferant"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notizen</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Zusätzliche Notizen..."
              className="w-full p-2 border border-slate-300 rounded-md"
              rows="3"
            />
          </div>

          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline" onClick={onBack}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              {loading ? 'Wird gespeichert...' : 'Artikel hinzufügen'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Edit Item Component  
const EditItemForm = ({ item, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: item.name || '',
    description: item.description || '',
    category: item.category || 'werkzeuge',
    subcategory: item.subcategory || '',
    serial_number: item.serial_number || '',
    inventory_number: item.inventory_number || '',
    current_stock: item.current_stock || 0,
    min_stock: item.min_stock || 0,
    max_stock: item.max_stock || '',
    unit: item.unit || 'Stück',
    purchase_price: item.purchase_price || '',
    current_value: item.current_value || '',
    supplier: item.supplier || '',
    location: item.location || 'hauptlager',
    location_details: item.location_details || '',
    status: item.status || 'verfügbar',
    manufacturer: item.manufacturer || '',
    model: item.model || '',
    notes: item.notes || ''
  });

  const [loading, setLoading] = useState(false);

  const categories = [
    { value: 'werkzeuge', label: 'Werkzeuge' },
    { value: 'maschinen', label: 'Maschinen' },
    { value: 'materialien', label: 'Materialien' },
    { value: 'verbrauchsartikel', label: 'Verbrauchsartikel' },
    { value: 'ersatzteile', label: 'Ersatzteile' },
    { value: 'sonstige', label: 'Sonstige' }
  ];

  const locations = [
    { value: 'hauptlager', label: 'Hauptlager' },
    { value: 'fahrzeug', label: 'Fahrzeug' },
    { value: 'baustelle', label: 'Baustelle' },
    { value: 'werkstatt', label: 'Werkstatt' },
    { value: 'aussenlager', label: 'Außenlager' },
    { value: 'sonstiges', label: 'Sonstiges' }
  ];

  const statusOptions = [
    { value: 'verfügbar', label: 'Verfügbar' },
    { value: 'in_verwendung', label: 'In Verwendung' },
    { value: 'wartung', label: 'Wartung' },
    { value: 'defekt', label: 'Defekt' },
    { value: 'ausgemustert', label: 'Ausgemustert' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      
      // Ensure numeric fields are sent as numbers, not strings
      const submitData = {
        ...formData,
        current_stock: parseInt(formData.current_stock) || 0,
        min_stock: parseInt(formData.min_stock) || 0,
        max_stock: formData.max_stock ? parseInt(formData.max_stock) : null,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        current_value: formData.current_value ? parseFloat(formData.current_value) : null
      };
      
      await axios.put(`${BACKEND_URL}/api/inventory/${item.id}`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Inventargegenstand erfolgreich aktualisiert');
      onSuccess();
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      console.error('Error response:', error.response?.data);
      
      // Better error message handling
      let errorMessage = 'Unbekannter Fehler';
      
      if (error.response?.data?.detail) {
        // Handle Pydantic validation errors (array of errors)
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map(err => `${err.loc?.join(' → ') || 'Feld'}: ${err.msg}`)
            .join('\n');
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error.response?.status === 403) {
        errorMessage = 'Keine Berechtigung. Nur Administratoren dürfen Inventargegenstände bearbeiten.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert('Fehler beim Aktualisieren:\n\n' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-4xl my-8">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Inventargegenstand bearbeiten</span>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-2">Kategorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full border rounded-md p-2"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Inventory Number */}
              <div>
                <label className="block text-sm font-medium mb-2">Inventarnummer</label>
                <Input
                  value={formData.inventory_number}
                  onChange={(e) => handleChange('inventory_number', e.target.value)}
                  placeholder="INV-2024-001"
                />
              </div>

              {/* Serial Number */}
              <div>
                <label className="block text-sm font-medium mb-2">Seriennummer</label>
                <Input
                  value={formData.serial_number}
                  onChange={(e) => handleChange('serial_number', e.target.value)}
                />
              </div>

              {/* Current Stock */}
              <div>
                <label className="block text-sm font-medium mb-2">Aktueller Bestand *</label>
                <Input
                  type="number"
                  value={formData.current_stock}
                  onChange={(e) => handleChange('current_stock', parseInt(e.target.value) || 0)}
                  required
                />
              </div>

              {/* Min Stock */}
              <div>
                <label className="block text-sm font-medium mb-2">Mindestbestand</label>
                <Input
                  type="number"
                  value={formData.min_stock}
                  onChange={(e) => handleChange('min_stock', parseInt(e.target.value) || 0)}
                />
              </div>

              {/* Unit */}
              <div>
                <label className="block text-sm font-medium mb-2">Einheit</label>
                <Input
                  value={formData.unit}
                  onChange={(e) => handleChange('unit', e.target.value)}
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full border rounded-md p-2"
                >
                  {statusOptions.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium mb-2">Lagerort</label>
                <select
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="w-full border rounded-md p-2"
                >
                  {locations.map(loc => (
                    <option key={loc.value} value={loc.value}>{loc.label}</option>
                  ))}
                </select>
              </div>

              {/* Manufacturer */}
              <div>
                <label className="block text-sm font-medium mb-2">Hersteller</label>
                <Input
                  value={formData.manufacturer}
                  onChange={(e) => handleChange('manufacturer', e.target.value)}
                />
              </div>

              {/* Model */}
              <div>
                <label className="block text-sm font-medium mb-2">Modell</label>
                <Input
                  value={formData.model}
                  onChange={(e) => handleChange('model', e.target.value)}
                />
              </div>

              {/* Supplier */}
              <div>
                <label className="block text-sm font-medium mb-2">Lieferant</label>
                <Input
                  value={formData.supplier}
                  onChange={(e) => handleChange('supplier', e.target.value)}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2">Beschreibung</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="w-full border rounded-md p-2 h-24"
                placeholder="Detaillierte Beschreibung des Gegenstands"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-2">Notizen</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="w-full border rounded-md p-2 h-20"
                placeholder="Zusätzliche Notizen"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Speichere...' : 'Änderungen speichern'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Inventory;